import React from "react"
import { Image, StyleSheet, View } from "react-native"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import moment from "moment"
import { formatHoursPerWeek } from "@common/utils/creditCalculation"
import { showsFirstMonthStep } from "../newSubscriptionUtils"
import appPreviewImage from "@assets/offers/app-preview.png"
import { formatEuros } from "../../../brevet/utils/formatEuros"
import { formatCollectionDay } from "@data/constants/studentsConstants"

export const NewSubscriptionAccompanimentCard = ({
    selectedSchoolSubjects,
    plan,
    amountToPay,
    hoursPerMonth,
    monthlyPrice,
    hourlyPrice,
    noDiscountPrice,
    discountAmount,
    firstMonth,
    schedule,
    paymentCollectionDay,
}) => {
    const paymentDay = paymentCollectionDay || 1
    // Le mois du DÉMARRAGE, pas le mois courant : une vente signée fin août pour
    // une rentrée en septembre datait le prorata du mauvais mois. Le serveur le
    // nomme (`first_month.month`, déjà en français) ; le repli local ne sert que
    // s'il se tait, et cet écran ne charge pas la locale fr de moment.
    const firstMonthLabel =
        firstMonth?.month ?? moment(plan?.startDate ?? undefined).format("MMMM")
    // La marche intermédiaire — le premier prélèvement, prorata du mois entamé —
    // ne vaut d'être annoncée que si le paiement du jour ne l'EST pas déjà.
    const showFirstMonth = showsFirstMonthStep({ amountToPay, firstMonth })

    return (
        <View style={[styles.card]}>
            <Image
                source={appPreviewImage}
                style={{
                    width: "100%",
                    height: 250,
                    borderRadius: 10,
                    marginTop: -50,
                    marginBottom: -20,
                }}
                resizeMode="contain"
            />
            <TextM
                fontWeight="bold"
                fontSize={20}
                color={theme.colors.black}
                wrap
            >
                Accompagnement Masteur
            </TextM>
            <TextM fontWeight="Medium" fontSize={16} wrap>
                {selectedSchoolSubjects
                    .map((subject) => subject.name)
                    .join(" & ")}{" "}
                - {formatHoursPerWeek(plan?.hoursPerWeek)}
                /semaine
            </TextM>

            <View style={styles.separator} />

            <TextM fontWeight="Medium" fontSize={16} color={theme.colors.black}>
                Détail du prix
            </TextM>
            {schedule ? (
                <ScheduleRows schedule={schedule} amountToPay={amountToPay} />
            ) : (
                <LegacyPriceRows
                    amountToPay={amountToPay}
                    firstMonthLabel={firstMonthLabel}
                    discountAmount={discountAmount}
                    noDiscountPrice={noDiscountPrice}
                    showFirstMonth={showFirstMonth}
                    firstMonth={firstMonth}
                    monthlyPrice={monthlyPrice}
                    hoursPerMonth={hoursPerMonth}
                    hourlyPrice={hourlyPrice}
                />
            )}

            <TextM
                fontSize={11}
                color={theme.colors.light}
                style={{ fontStyle: "italic" }}
                wrap
            >
                Prélèvements mensuels le {formatCollectionDay(paymentDay)}{" "}
                de chaque mois. Date modifiable depuis votre espace parent.
            </TextM>

            <View style={styles.separator} />

            <View style={styles.row}>
                <TextM fontSize={18} fontWeight="Bold">
                    Total à payer aujourd'hui
                </TextM>
                <TextM
                    fontSize={24}
                    color={theme.colors.primary}
                    fontWeight="Bold"
                >
                    {amountToPay}€
                </TextM>
            </View>
            {!schedule && (
                <TextM
                    fontSize={14}
                    fontWeight="Medium"
                    color={theme.colors.grey}
                    wrap
                >
                    {showFirstMonth
                        ? `Puis ${firstMonth.amount}€ au premier cours, puis ${monthlyPrice}€/mois`
                        : `Puis ${monthlyPrice}€/mois`}
                </TextM>
            )}
        </View>
    )
}

/**
 * L'échéancier d'une vente BO, tel que le serveur le facturera — remise
 * répartie comprise. Aucune ligne n'est recalculée ici.
 */
const ScheduleRows = ({ schedule, amountToPay }) => (
    <>
        <View style={styles.row}>
            <TextM fontSize={16} color={theme.colors.black} fontWeight="Medium">
                {amountToPay > 0
                    ? "Aujourd'hui : pré-inscription"
                    : "Aujourd'hui : vérification de carte"}
            </TextM>
            <TextM fontSize={18} fontWeight="Bold">
                {amountToPay}€
            </TextM>
        </View>
        {schedule.echeances.map((row) => (
            <View key={`${row.kind}-${row.month}`} style={{ gap: 2 }}>
                <View style={styles.row}>
                    <TextM fontSize={15} color={theme.colors.black} wrap>
                        {row.label}
                    </TextM>
                    <View style={styles.row}>
                        {row.discount > 0 && (
                            <TextM
                                fontWeight="Medium"
                                style={{ textDecorationLine: "line-through" }}
                            >
                                {formatEuros(row.gross)}
                            </TextM>
                        )}
                        <TextM fontSize={16} fontWeight="Bold">
                            {formatEuros(row.amount)}
                        </TextM>
                    </View>
                </View>
                <TextM fontSize={12} color={theme.colors.grey} wrap>
                    {row.kind === "recurring"
                        ? `${row.hours}h par mois, chaque mois`
                        : `Prélevé le ${moment(row.charge_on).format("DD/MM")}`}
                    {row.discount > 0
                        ? ` · remise de ${formatEuros(row.discount)}`
                        : ""}
                </TextM>
            </View>
        ))}
    </>
)

const LegacyPriceRows = ({
    amountToPay,
    firstMonthLabel,
    discountAmount,
    noDiscountPrice,
    showFirstMonth,
    firstMonth,
    monthlyPrice,
    hoursPerMonth,
    hourlyPrice,
}) => (
    <>
        <View style={styles.row}>
            <TextM fontSize={16} color={theme.colors.black} fontWeight="Medium">
                {amountToPay === 1
                    ? "Pré-inscription"
                    : `Prorata ${firstMonthLabel}`}
            </TextM>
            <View style={styles.row}>
                {!!discountAmount && (
                    <TextM
                        fontWeight="Medium"
                        style={{ textDecorationLine: "line-through" }}
                    >
                        {Math.round(noDiscountPrice)}€
                    </TextM>
                )}
                <TextM fontSize={18} fontWeight="Bold">
                    {amountToPay}€
                </TextM>
            </View>
        </View>
        {showFirstMonth && (
            <TextM fontSize={14} wrap>
                puis {firstMonth.amount}€ au premier cours — {firstMonth.hours}h
                en {firstMonthLabel}
            </TextM>
        )}
        <TextM fontSize={14} wrap>
            puis {monthlyPrice}€/mois pour {hoursPerMonth}h/mois (soit{" "}
            {Math.round(hourlyPrice)}€/h)
        </TextM>
    </>
)

const styles = StyleSheet.create({
    card: {
        backgroundColor: theme.colors.white,
        padding: 20,
        borderRadius: 20,
        gap: 15,
        elevation: 4,
        shadowColor: theme.colors.black,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
    },
    separator: {
        height: 1,
        backgroundColor: theme.colors.lightMore,
    },
    row: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
    },
})
